import React from 'react';
import { ShieldCheck, AlertTriangle, Lock, FileSearch, ScrollText } from 'lucide-react';
import { SectionHeader } from '@/components/shared/SectionHeader';
import { KpiCard } from '@/components/shared/KpiCard';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { COMPLIANCE_RULES } from '@/data/seed';
import { useApp } from '@/context/AppContext';
import { usePageTitle } from '@/lib/usePageTitle';

const BLOCKED_EVENTS = [
  { id: 'BLK-001', case: 'CASE-10402', kind: 'WhatsApp', reason: 'Debtor opted-out • active complaint', when: '4m ago', tone: 'destructive' },
  { id: 'BLK-002', case: 'CASE-10419', kind: 'Call', reason: 'Outside contact window (22:30)', when: '12m ago', tone: 'destructive' },
  { id: 'BLK-003', case: 'CASE-10422', kind: 'SMS', reason: 'Dispute hold — contact blocked', when: '34m ago', tone: 'warning' },
  { id: 'BLK-004', case: 'CASE-10428', kind: 'WhatsApp', reason: 'Max attempts/day exceeded (3)', when: '1h ago', tone: 'warning' },
];

export default function Compliance() {
  usePageTitle('Compliance & QA');
  return (
    <div className="space-y-6 animate-fade-in">
      <SectionHeader
        eyebrow="Compliance & QA"
        title="Policy engine & audit posture"
        subtitle="Configurable contact rules, consent enforcement, blocked outreach evidence, complaint/dispute handling and PDPL data controls."
        actions={<Button variant="outline" size="sm"><FileSearch className="h-4 w-4 mr-1.5" />Open audit</Button>}
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Blocked attempts (24h)" value={32} format="num" trend={-12} icon={Lock} />
        <KpiCard label="Active disputes" value={9} format="num" trend={2} icon={AlertTriangle} />
        <KpiCard label="Complaints" value={4} format="num" trend={-1} />
        <KpiCard label="QA score (team avg)" value={92.4} format="pct" trend={1.6} icon={ShieldCheck} />
      </div>

      <div className="grid lg:grid-cols-5 gap-4">
        {/* Policy engine */}
        <div className="surface-card p-5 lg:col-span-3">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-primary">Policy engine</p>
              <h3 className="text-base font-semibold mt-0.5">Active contact compliance rules</h3>
            </div>
            <Badge variant="outline" className="text-success border-success/40 bg-success/10">SAMA-aligned</Badge>
          </div>
          <div className="space-y-2.5">
            {COMPLIANCE_RULES.map((r) => (
              <div key={r.id} className="flex items-center justify-between p-3 rounded-lg border border-border bg-muted/40">
                <div>
                  <p className="text-sm font-medium" id={`rule-${r.id}-label`}>{r.label}</p>
                  <p className="text-[11px] text-muted-foreground" id={`rule-${r.id}-desc`}>{r.value}</p>
                </div>
                <Switch
                  checked={r.enabled}
                  aria-label={`${r.label} — ${r.enabled ? 'enabled' : 'disabled'}`}
                  aria-describedby={`rule-${r.id}-desc`}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Regulatory readiness */}
        <div className="surface-card p-5 lg:col-span-2">
          <p className="text-[10px] uppercase tracking-widest text-primary">Regulatory readiness</p>
          <h3 className="text-base font-semibold mt-0.5 mb-3">Control mapping placeholders</h3>
          <div className="space-y-2">
            {[
              { k: 'SAMA license', v: 'On file • prototype placeholder', tone: 'success' },
              { k: 'PDPL DPIA', v: 'Draft v0.4', tone: 'warning' },
              { k: 'NCA controls', v: 'Mapped to ECC-1 • 22 controls', tone: 'success' },
              { k: 'Business continuity', v: 'Approved Q2', tone: 'success' },
              { k: 'Incident response', v: 'Tabletop scheduled', tone: 'warning' },
              { k: 'Third-party risk', v: '11 active vendors', tone: 'success' },
            ].map((r) => (
              <div key={r.k} className="flex items-center justify-between text-xs px-3 py-2 rounded-md border border-border bg-card">
                <span className="text-muted-foreground">{r.k}</span>
                <Badge variant="outline" className={r.tone === 'success' ? 'bg-success/10 text-success border-success/40' : 'bg-warning/10 text-warning border-warning/40'}>{r.v}</Badge>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Blocked events */}
      <div className="surface-card overflow-hidden">
        <div className="p-5 flex items-center justify-between border-b border-border">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-primary">Blocked contact attempts</p>
            <h3 className="text-base font-semibold mt-0.5">Policy enforcement evidence</h3>
          </div>
          <Badge variant="outline" className="text-[10px]">Last 24 hours</Badge>
        </div>
        <div className="divide-y divide-border">
          {BLOCKED_EVENTS.map((b) => (
            <div key={b.id} className="px-5 py-3 grid grid-cols-12 items-center text-sm">
              <div className="col-span-1"><Lock className="h-4 w-4 text-destructive" /></div>
              <div className="col-span-3 font-medium">{b.case}</div>
              <div className="col-span-2 text-muted-foreground">{b.kind}</div>
              <div className="col-span-4 text-muted-foreground">{b.reason}</div>
              <div className="col-span-1 text-[11px] text-muted-foreground">{b.when}</div>
              <div className="col-span-1 text-right"><Button variant="ghost" size="sm" className="text-xs">Mark reviewed</Button></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
